import { useQuery } from '@tanstack/vue-query'
import { useProductStore } from '@/stores/product'
import type { CategoryProduct, GeneralResponse, Product } from '@/types'
import { queryKeys } from './keys'

export function useProductsQuery() {
  const productStore = useProductStore()

  const productsQuery = useQuery({
    queryKey: queryKeys.products(),
    queryFn: () => apiGet<GeneralResponse<Product[]>>('products')
  })

  watchEffect(() => {
    if (productsQuery.data.value?.data) {
      productStore.setProduct(productsQuery.data.value.data)
    }
  })

  const products = computed(() => productsQuery.data.value?.data ?? [])

  return { products, productsQuery }
}

export function useCategoriesQuery() {
  const productStore = useProductStore()

  const categoriesQuery = useQuery({
    queryKey: queryKeys.categories(),
    queryFn: () => apiGet<GeneralResponse<CategoryProduct[]>>('categories')
  })

  watchEffect(() => {
    if (categoriesQuery.data.value?.data) {
      productStore.setCategories(categoriesQuery.data.value.data)
    }
  })

  const categories = computed(() => categoriesQuery.data.value?.data ?? [])

  return { categories, categoriesQuery }
}

export function useBikesCategoriesQuery() {
  const productStore = useProductStore()

  const bikesQuery = useQuery({
    queryKey: queryKeys.bikesCategories(),
    queryFn: () => apiGet<GeneralResponse<CategoryProduct[]>>('bikes-categories')
  })

  watchEffect(() => {
    if (bikesQuery.data.value?.data) {
      productStore.$patch({ bikesCategories: bikesQuery.data.value.data })
    }
  })

  const bikesCategories = computed(() => bikesQuery.data.value?.data ?? [])

  return { bikesCategories, bikesQuery }
}

export async function useProductsAndCategoriesQueries() {
  const { products, productsQuery } = useProductsQuery()
  const { categories, categoriesQuery } = useCategoriesQuery()

  await Promise.all([productsQuery.suspense(), categoriesQuery.suspense()])

  return { products, categories, productsQuery, categoriesQuery }
}

export async function useCategoriesCatalogQuery() {
  const { categories, categoriesQuery } = useCategoriesQuery()

  await categoriesQuery.suspense()

  return { categories, categoriesQuery }
}

export async function useBikesCategoriesCatalogQuery() {
  const { bikesCategories, bikesQuery } = useBikesCategoriesQuery()

  await bikesQuery.suspense()

  return { bikesCategories, bikesQuery }
}
